import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminBuild } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Filter } from "@/ui/Filter";
import { Chip, type ChipTone } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { Button } from "@/ui/Button";
import { StaffBuildLogDialog } from "@/features/deployments/StaffBuildLogDialog";
import { absoluteTime, duration, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/builds")({
  component: PlatformBuildsScreen,
});

const STATUSES = ["", "failed", "running", "queued", "succeeded", "cancelled"] as const;

const TONE: Record<string, ChipTone | undefined> = {
  succeeded: "up",
  failed: "down",
  running: "live",
  queued: undefined,
  cancelled: undefined,
};

/**
 * Every build, across every tenant.
 *
 * A failing build is the most common thing a customer writes in about, and
 * until this screen existed seeing one meant already knowing whose it was. The
 * default is every build; the first filter anybody reaches for is "failed".
 */
function PlatformBuildsScreen() {
  const [status, setStatus] = useState("");
  // Which build's output is open. The whole row is not a link: a build has one
  // thing worth opening and it is the log, so the log is the action.
  const [watching, setWatching] = useState<AdminBuild | null>(null);

  const builds = useQuery({
    queryKey: ["platform-builds", status],
    queryFn: ({ signal }) =>
      api.get<{ builds: AdminBuild[] | null }>(
        "/admin/builds",
        { status: status || undefined, limit: 200 },
        signal,
      ),
  });

  const list = builds.data?.builds ?? [];

  return (
    <div className="flex flex-col gap-4">

      <Panel
        toolbar={
          <>
            {STATUSES.map((s) => (
              <Filter
                key={s || "all"}
                label={s || "all"}
                active={status === s}
                onClick={() => setStatus(s)}
              />
            ))}
          </>
        }
        padded={false}
      >
        {builds.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={8} columns={5} />
          </div>
        ) : builds.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={builds.error} onRetry={() => void builds.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              title="No builds"
              description={status ? `Nothing is ${status}.` : "Nothing has been built yet."}
            />
          </div>
        ) : (
          <Table>
            <thead>
              <Tr>
                <Th>Build</Th>
                <Th>Tenant</Th>
                <Th>Source</Th>
                <Th>Status</Th>
                <Th align="right">Took</Th>
                <Th>When</Th>
                <Th>Output</Th>
              </Tr>
            </thead>
            <tbody>
              {list.map((b) => (
                <Tr key={b.id}>
                  <Td>
                    <span className="font-medium text-[var(--text-primary)]">#{b.number}</span>
                    <span className="ml-2 text-[13px] text-[var(--text-muted)]">
                      {b.service_name}
                    </span>
                  </Td>
                  <Td>
                    <Link
                      to="/admin/$orgId"
                      params={{ orgId: b.organisation_id }}
                      className="hover:underline"
                    >
                      {b.organisation_name}
                    </Link>
                  </Td>
                  <Td className="mono text-[13px]">
                    {b.repository ? (
                      <>
                        {b.repository}
                        {b.commit && (
                          <span className="text-[var(--text-muted)]">
                            {" "}
                            @ {b.commit.slice(0, 8)}
                          </span>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>
                    <Chip tone={TONE[b.status] ?? undefined}>{b.status}</Chip>
                    {b.status_reason && (
                      <p className="mt-1 max-w-sm text-[13px] text-[var(--text-muted)]">
                        {b.status_reason}
                      </p>
                    )}
                  </Td>
                  <Td align="right" className="mono text-[13px]">
                    {b.duration_seconds != null ? duration(b.duration_seconds) : "—"}
                  </Td>
                  <Td>
                    <span title={absoluteTime(b.created_at)}>{relativeTime(b.created_at)}</span>
                  </Td>
                  <Td>
                    <Button size="dense" tone="secondary" onClick={() => setWatching(b)}>
                      {b.status === "running" || b.status === "queued" ? "Watch" : "Log"}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      {watching && (
        <StaffBuildLogDialog build={watching} onClose={() => setWatching(null)} />
      )}
    </div>
  );
}
